import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useOdooAuth } from '@/lib/OdooAuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Plus, ShoppingCart, Search, X, ChevronDown,
  Eye, Edit3, Printer, Loader2, AlertCircle
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import EmptyState from '@/components/shared/EmptyState';
import { api } from '@/api/apiClient';
import { cur } from '@/lib/currency';

const PAGE_SIZE = 50;

const STATUS_MAP = { draft: 'مسودة', sent: 'مرسل', sale: 'مؤكد', cancel: 'ملغي', done: 'منتهي' };
const STATUS_STYLE = {
  draft:  'bg-amber-50 text-amber-700 border border-amber-200',
  sent:   'bg-blue-50 text-blue-700 border border-blue-200',
  sale:   'bg-emerald-50 text-emerald-700 border border-emerald-200',
  cancel: 'bg-red-50 text-red-700 border border-red-200',
  done:   'bg-gray-100 text-gray-600 border border-gray-200',
};
const STATUS_DOT = {
  draft: 'bg-amber-400', sent: 'bg-blue-400', sale: 'bg-emerald-500', cancel: 'bg-red-500', done: 'bg-gray-400',
};

const PAYMENT_MAP = {
  paid:       { label: 'مدفوع',           style: 'bg-emerald-50 text-emerald-700 border border-emerald-200' },
  partial:    { label: 'مدفوع جزئياً',    style: 'bg-orange-50 text-orange-700 border border-orange-200' },
  not_paid:   { label: 'غير مدفوع',       style: 'bg-red-50 text-red-600 border border-red-200' },
  overdue:    { label: 'متأخر',           style: 'bg-red-100 text-red-700 border border-red-300' },
  to_invoice: { label: 'لم يُفوتر',       style: 'bg-amber-50 text-amber-700 border border-amber-200' },
  no_invoice: { label: 'لا فاتورة',       style: 'bg-gray-50 text-gray-500 border border-gray-200' },
};

const PERIOD_FILTERS = [
  { key: 'today',       label: 'اليوم' },
  { key: 'yesterday',   label: 'أمس' },
  { key: 'this_week',   label: 'هذا الأسبوع' },
  { key: 'this_month',  label: 'هذا الشهر' },
];

const SORT_OPTIONS = [
  { key: 'newest',      label: 'الأحدث أولاً' },
  { key: 'oldest',      label: 'الأقدم أولاً' },
  { key: 'amount_desc', label: 'الأعلى مبلغاً' },
  { key: 'partner_asc', label: 'اسم العميل أ-ي' },
];

function getPeriodDates(period) {
  // Cairo = UTC+2
  const offset = 2 * 60 * 60 * 1000;
  const now = new Date(Date.now() + offset);
  const todayStr = now.toISOString().slice(0, 10);

  if (period === 'today') return { date_from: `${todayStr} 00:00:00`, date_to: `${todayStr} 23:59:59` };
  if (period === 'yesterday') {
    const y = new Date(now); y.setDate(y.getDate() - 1);
    const ys = y.toISOString().slice(0, 10);
    return { date_from: `${ys} 00:00:00`, date_to: `${ys} 23:59:59` };
  }
  if (period === 'this_week') {
    const day = now.getDay();
    const sat = new Date(now); sat.setDate(now.getDate() - ((day + 1) % 7));
    return { date_from: `${sat.toISOString().slice(0, 10)} 00:00:00`, date_to: `${todayStr} 23:59:59` };
  }
  if (period === 'this_month') {
    return { date_from: `${todayStr.slice(0, 7)}-01 00:00:00`, date_to: `${todayStr} 23:59:59` };
  }
  return {};
}

function SkeletonCard() {
  return (
    <div className="bg-card border border-border rounded-2xl p-4 space-y-3 animate-pulse">
      <div className="flex justify-between">
        <div className="h-5 bg-muted rounded w-24" />
        <div className="h-5 bg-muted rounded w-16" />
      </div>
      <div className="h-4 bg-muted rounded w-40" />
      <div className="h-4 bg-muted rounded w-32" />
      <div className="flex justify-between">
        <div className="h-6 bg-muted rounded w-28" />
        <div className="h-6 bg-muted rounded w-20" />
      </div>
    </div>
  );
}

function StatStrip({ stats, loading }) {
  if (loading) {
    return (
      <div className="grid grid-cols-2 gap-2">
        {[0, 1].map(i => <div key={i} className="bg-card border border-border rounded-xl p-3 animate-pulse h-16" />)}
      </div>
    );
  }
  if (!stats) return null;
  const fmt = (n) => Number(n || 0).toLocaleString('ar-EG', { maximumFractionDigits: 0 });
  return (
    <div className="grid grid-cols-2 gap-2">
      <div className="bg-primary/5 border border-primary/20 rounded-xl p-3">
        <p className="text-xs text-muted-foreground mb-0.5">اليوم</p>
        <p className="font-bold text-primary text-sm">{stats.today_count} طلب</p>
        <p className="text-xs text-muted-foreground">{fmt(stats.today_amount)} {cur()}</p>
      </div>
      <div className="bg-emerald-50 border border-emerald-100 rounded-xl p-3">
        <p className="text-xs text-muted-foreground mb-0.5">هذا الشهر</p>
        <p className="font-bold text-emerald-700 text-sm">{stats.month_count} طلب</p>
        <p className="text-xs text-muted-foreground">{fmt(stats.month_amount)} {cur()}</p>
      </div>
    </div>
  );
}

function OrderCard({ order, onView, onEdit, showRep = false }) {
  const partnerName = Array.isArray(order.partner_id) ? order.partner_id[1] : (order.partner_id || '—');
  const repName = Array.isArray(order.user_id) ? order.user_id[1] : '';
  const lineCount = Array.isArray(order.order_line) ? order.order_line.length : 0;
  const payInfo = PAYMENT_MAP[order.computed_payment_status] || null;
  const dateStr = order.date_order
    ? new Date(order.date_order.replace(' ', 'T')).toLocaleDateString('ar-EG', { day: 'numeric', month: 'short', year: 'numeric' })
    : '—';
  const timeStr = order.date_order
    ? new Date(order.date_order.replace(' ', 'T')).toLocaleTimeString('ar-EG', { hour: '2-digit', minute: '2-digit' })
    : '';
  const isDraft = order.state === 'draft';

  return (
    <div
      className="bg-card border border-border rounded-2xl p-4 space-y-3 hover:shadow-md hover:border-primary/20 transition-all cursor-pointer active:scale-[0.99]"
      onClick={() => onView(order.id)}
    >
      {/* Row 1: order number + state badge + date */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex items-center gap-2 flex-wrap">
          <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full ${STATUS_STYLE[order.state] || STATUS_STYLE.draft}`}>
            <span className={`w-1.5 h-1.5 rounded-full ${STATUS_DOT[order.state] || 'bg-gray-400'}`} />
            {STATUS_MAP[order.state] || order.state}
          </span>
          {payInfo && order.state !== 'draft' && (
            <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${payInfo.style}`}>{payInfo.label}</span>
          )}
        </div>
        <div className="text-left flex-shrink-0">
          <p className="text-xs font-semibold text-foreground">{order.name}</p>
          <p className="text-xs text-muted-foreground">{dateStr}</p>
          {timeStr && <p className="text-[11px] text-muted-foreground">{timeStr}</p>}
        </div>
      </div>

      {/* Row 2: customer */}
      <div className="flex items-center gap-2">
        <div className="w-7 h-7 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
          <span className="text-primary font-bold text-xs">{partnerName.charAt(0)}</span>
        </div>
        <p className="text-sm font-semibold text-foreground truncate">{partnerName}</p>
        {showRep && repName && (
          <span className="text-xs bg-amber-50 text-amber-700 border border-amber-200 rounded-full px-2 py-0.5 font-semibold flex-shrink-0 mr-auto">🧭 {repName}</span>
        )}
      </div>

      {/* Row 3: lines count + amount */}
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-1.5">
          <ShoppingCart className="w-3.5 h-3.5 text-muted-foreground" />
          <span className="text-xs text-muted-foreground">
            {lineCount > 0 ? `${lineCount} صنف` : 'لا أصناف'}
          </span>
        </div>
        <p className="font-bold text-primary text-lg leading-none">
          {Number(order.amount_total || 0).toLocaleString('ar-EG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          <span className="text-xs font-normal mr-0.5">{cur()}</span>
        </p>
      </div>

      {/* Row 4: action buttons */}
      <div className="flex gap-2 pt-1 border-t border-border" onClick={e => e.stopPropagation()}>
        <button
          onClick={() => onView(order.id)}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-primary hover:bg-primary/5 rounded-lg transition-colors"
        >
          <Eye className="w-3.5 h-3.5" /> عرض
        </button>
        {isDraft && (
          <button
            onClick={() => onEdit(order.id)}
            className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-colors"
          >
            <Edit3 className="w-3.5 h-3.5" /> تعديل
          </button>
        )}
        <button
          onClick={() => window.print()}
          className="flex-1 flex items-center justify-center gap-1.5 py-1.5 text-xs font-medium text-muted-foreground hover:text-foreground hover:bg-muted rounded-lg transition-colors"
        >
          <Printer className="w-3.5 h-3.5" /> طباعة
        </button>
      </div>
    </div>
  );
}

export default function Orders() {
  const navigate = useNavigate();
  const { odooSession } = useOdooAuth();

  const [orders, setOrders] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [stateFilter, setStateFilter] = useState('all');
  const [periodFilter, setPeriodFilter] = useState(null);
  const [sortBy, setSortBy] = useState('newest');
  const [showSort, setShowSort] = useState(false);
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);

  const searchTimerRef = useRef(null);

  // Load stats. Reps see stats scoped to themselves; managers see
  // company-wide stats. Prefer the SellGo-scoped endpoint because the
  // legacy `/sales/orders/stats` returns company-wide state_counts even
  // when given a salesman_uid.
  useEffect(() => {
    setLoadingStats(true);
    const isManager = odooSession?.role === 'manager';
    const isRep = odooSession?.role === 'rep';
    const params = { salesman_uid: isRep ? odooSession?.uid : undefined };
    (async () => {
      let res;
      try {
        res = await api.get('/sellgo_so_stats', params);
      } catch {
        try {
          res = await api.get('/sellgo_orders_stats', params);
        } catch {
          // The legacy endpoint returns COMPANY-WIDE counts regardless of
          // salesman_uid, which is wrong for a rep/supervisor. Only managers
          // may fall back to it; others get empty counts rather than leaked
          // company totals.
          res = isManager ? await api.get('/sales/orders/stats', params) : null;
        }
      }
      if (res?.success) setStats(res.data);
      setLoadingStats(false);
    })().catch(() => setLoadingStats(false));
  }, [odooSession?.uid, odooSession?.role]);

  const buildParams = useCallback((pg) => {
    const periodDates = periodFilter ? getPeriodDates(periodFilter) : {};
    // Reps see only their own orders. Managers see everything.
    // The legacy `/sales/orders` endpoint in `odoo_dashboard_api_v4`
    // honours `salesman_uid` (already used by `/sales/orders/stats`).
    const isRep = odooSession?.role === 'rep';
    return {
      action: 'list',
      state_filter: stateFilter !== 'all' ? stateFilter : undefined,
      search: search.trim() || undefined,
      sort_by: sortBy !== 'newest' ? sortBy : undefined,
      limit: PAGE_SIZE,
      page: pg,
      salesman_uid: isRep ? odooSession?.uid : undefined,
      ...periodDates,
    };
  }, [stateFilter, search, sortBy, periodFilter, odooSession?.role, odooSession?.uid]);

  const loadOrders = useCallback(async (pg = 0, append = false) => {
    if (append) setLoadingMore(true);
    else setLoading(true);
    setError(null);
    try {
      // buildParams() returns { action: 'list', state_filter, search, ... } —
      // drop `action` since the path itself encodes the resource.
      const { action: _unused, ...params } = buildParams(pg);
      // Prefer the SellGo endpoint that respects user scoping. The
      // legacy `/sales/orders` in v4 ignores `salesman_uid` and a v4
      // catch-all also blocks any URL containing the word "orders",
      // so we use a deliberately short/unique path.
      let res;
      try {
        res = await api.get('/sellgo_so', params);
      } catch {
        try {
          res = await api.get('/sellgo_so_list', params);
        } catch {
          // Legacy `/sales/orders` ignores scoping (company-wide), so only
          // managers may use it. For reps/supervisors we degrade to an empty
          // result rather than leaking everyone's orders or showing an error.
          if (odooSession?.role === 'manager') {
            res = await api.get('/sales/orders', params);
          } else {
            res = { success: true, data: [], total: 0 };
          }
        }
      }
      if (res?.success) {
        const data = res.data || [];
        setOrders(prev => append ? [...prev, ...data] : data);
        setTotal(res.total || 0);
      } else if (odooSession?.role === 'manager') {
        throw new Error(res?.error || 'فشل التحميل');
      } else {
        // Non-managers: never show a scary error or company-wide data.
        if (!append) { setOrders([]); setTotal(0); }
      }
    } catch (e) {
      setError(e.message);
    }
    if (append) setLoadingMore(false);
    else setLoading(false);
  }, [buildParams]);

  // Reset + reload when filters change
  useEffect(() => {
    setPage(0);
    loadOrders(0, false);
  }, [stateFilter, periodFilter, sortBy]);

  // Debounced search
  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimerRef.current);
    searchTimerRef.current = setTimeout(() => {
      setPage(0);
      loadOrders(0, false);
    }, 350);
  };

  const loadMore = () => {
    const next = page + 1;
    setPage(next);
    loadOrders(next, true);
  };

  const stateCounts = stats?.state_counts || {};
  const STATE_FILTERS = [
    { key: 'all',    label: 'الكل',   count: stateCounts.all },
    { key: 'draft',  label: 'مسودة',  count: stateCounts.draft },
    { key: 'sale',   label: 'مؤكد',   count: stateCounts.sale },
    { key: 'done',   label: 'منتهي',  count: stateCounts.done },
    { key: 'cancel', label: 'ملغي',   count: stateCounts.cancel },
  ];

  const hasMore = orders.length < total;

  return (
    <div className="flex flex-col h-full" dir="rtl">
      {/* ── Sticky header ── */}
      <div className="sticky top-0 z-20 bg-background/95 backdrop-blur-sm border-b border-border">
        {/* Title row */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <div className="flex items-center gap-2">
            <h1 className="text-xl font-bold text-foreground">أوامر البيع</h1>
            <span className="text-xs font-semibold bg-primary/10 text-primary px-2.5 py-1 rounded-full">
              {total.toLocaleString('ar-EG')}
            </span>
          </div>
          <Button onClick={() => navigate('/orders/new')} size="sm" className="bg-primary gap-1.5 h-9">
            <Plus className="w-4 h-4" /> جديد
          </Button>
        </div>

        {/* Search */}
        <div className="px-4 pb-2">
          <div className="relative">
            <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => handleSearch(e.target.value)}
              placeholder="بحث برقم الطلب أو اسم العميل..."
              className="pr-10 bg-card rounded-xl border-border"
            />
            {search && (
              <button onClick={() => handleSearch('')} className="absolute left-3 top-1/2 -translate-y-1/2">
                <X className="w-4 h-4 text-muted-foreground" />
              </button>
            )}
          </div>
        </div>

        {/* State filter chips */}
        <div className="flex items-center gap-1.5 px-4 pb-2 overflow-x-auto no-scrollbar">
          {STATE_FILTERS.map(f => (
            <button key={f.key} onClick={() => setStateFilter(f.key)}
              className={`flex-shrink-0 flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded-full transition-all ${
                stateFilter === f.key ? 'bg-primary text-white' : 'bg-card border border-border text-muted-foreground hover:border-primary/30'
              }`}>
              {f.label}
              {f.count !== undefined && (
                <span className={`text-xs px-1 rounded-md ${stateFilter === f.key ? 'bg-white/20 text-white' : 'bg-muted text-muted-foreground'}`}>
                  {Number(f.count || 0).toLocaleString('ar-EG')}
                </span>
              )}
            </button>
          ))}
        </div>

        {/* Period filters + Sort */}
        <div className="flex items-center justify-between px-4 pb-3 gap-2">
          <div className="flex items-center gap-1.5 overflow-x-auto no-scrollbar flex-1">
            {PERIOD_FILTERS.map(p => (
              <button key={p.key} onClick={() => setPeriodFilter(prev => prev === p.key ? null : p.key)}
                className={`flex-shrink-0 text-xs font-medium px-2.5 py-1.5 rounded-full transition-all ${
                  periodFilter === p.key ? 'bg-blue-600 text-white' : 'bg-card border border-border text-muted-foreground'
                }`}>
                {p.label}
              </button>
            ))}
          </div>

          {/* Sort dropdown */}
          <div className="relative flex-shrink-0">
            <button onClick={() => setShowSort(s => !s)}
              className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 bg-card border border-border rounded-full text-muted-foreground hover:border-primary/30 transition-all">
              {SORT_OPTIONS.find(s => s.key === sortBy)?.label}
              <ChevronDown className={`w-3 h-3 transition-transform ${showSort ? 'rotate-180' : ''}`} />
            </button>
            <AnimatePresence>
              {showSort && (
                <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -5 }}
                  className="absolute left-0 top-full mt-1 bg-card border border-border rounded-xl shadow-lg z-50 min-w-[150px] overflow-hidden">
                  {SORT_OPTIONS.map(s => (
                    <button key={s.key} onClick={() => { setSortBy(s.key); setShowSort(false); }}
                      className={`w-full text-right px-4 py-2.5 text-xs font-medium transition-colors ${
                        sortBy === s.key ? 'bg-primary/10 text-primary' : 'text-foreground hover:bg-muted'
                      }`}>
                      {s.label}
                    </button>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* ── Content ── */}
      <div className="flex-1 overflow-y-auto p-4 space-y-3 pb-8" onClick={() => showSort && setShowSort(false)}>
        {/* Stats strip */}
        <StatStrip stats={stats} loading={loadingStats} />

        {loading ? (
          <div className="space-y-3">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3">
            <AlertCircle className="w-12 h-12 text-red-400" />
            <p className="font-semibold text-foreground">{error}</p>
            <Button onClick={() => loadOrders(0)} variant="outline" size="sm">إعادة المحاولة</Button>
          </div>
        ) : orders.length === 0 ? (
          <EmptyState icon={ShoppingCart} title="لا توجد طلبات"
            description="لم يتم العثور على طلبات بهذه المعايير"
            action={
              <Button onClick={() => { setStateFilter('all'); setPeriodFilter(null); setSearch(''); }} variant="outline" size="sm">
                مسح الفلاتر
              </Button>
            }
          />
        ) : (
          <>
            <div className="space-y-3">
              {orders.map((order, i) => (
                <motion.div key={order.id}
                  initial={{ opacity: 0, y: 8 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(i * 0.015, 0.25) }}
                >
                  <OrderCard
                    order={order}
                    onView={id => navigate(`/orders/${id}`)}
                    onEdit={id => navigate(`/orders/${id}`)}
                    showRep={odooSession?.role !== 'rep'}
                  />
                </motion.div>
              ))}
            </div>

            {hasMore && (
              <Button onClick={loadMore} variant="outline" className="w-full rounded-xl" disabled={loadingMore}>
                {loadingMore
                  ? <><Loader2 className="w-4 h-4 animate-spin ml-2" /> جاري التحميل...</>
                  : `تحميل المزيد (${(total - orders.length).toLocaleString('ar-EG')} متبقي)`
                }
              </Button>
            )}

            <p className="text-center text-xs text-muted-foreground pb-2">
              {orders.length.toLocaleString('ar-EG')} من {total.toLocaleString('ar-EG')} طلب
            </p>
          </>
        )}
      </div>
    </div>
  );
}